import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import React from 'react';

import UsersClient from 'src/clients/UsersClient';
import {
  SIGNED_IN_WITH_GOOGLE_USER_KEY,
  SIGNED_IN_WITH_PROXY_USER_KEY,
} from 'src/constants/authConsts';
import { PortalConfigContext } from 'src/context';
import {
  closeSessionAndEmitEnded,
  parsePersisted,
  SESSION_STATE_KEY_PREFIX,
} from 'src/hooks/useActivitySessionTracker';
import { useAppSelector } from 'src/hooks/useStore';
import { usePostAuditEventMutation } from 'src/services/api/auditLogApi';
import { RootState } from 'src/store';
import { API } from 'src/utils/AmplifyApiUtils';
import { TrackEvent } from 'src/utils/AnalyticsUtils';
import { logoutFromHostedUi } from 'src/utils/AuthUtils';
import { resetPosthogIdentity } from 'src/utils/posthogIdentity';
import { isEmptyString } from 'src/utils/StringUtils';

export const useLogout = () => {
  const portalConfig = React.useContext(PortalConfigContext);
  const actorId = useAppSelector((state: RootState) => state.user.id);
  const [postAuditEvent] = usePostAuditEventMutation();

  const emitLogoutEvent = () =>
    API.post('AppAPI', '/events', {
      body: {
        eventType: 'userLoggedOut',
        eventDescription: 'User logged out',
        context: {},
      },
    }).catch(() => {
      // fire-and-forget — audit failures must not block logout
    });

  // Reads the active session snapshot from localStorage and closes it. The
  // 15-min inactivity timer can't fire during logout (the tab unloads
  // immediately after Auth.signOut), so without this the open `sessionStarted`
  // would never get a matching `sessionEnded` until the next login triggers
  // backend orphan-backfill.
  const endSessionForLogout = () => {
    const portalId = portalConfig.portalHeader;
    if (!portalId || !actorId) return;
    const key = `${SESSION_STATE_KEY_PREFIX}${portalId}_${actorId}`;
    let persisted;
    try {
      persisted = parsePersisted(localStorage.getItem(key));
    } catch {
      return;
    }
    if (
      !persisted ||
      persisted.state !== 'active' ||
      persisted.sessionStartedAt === 0 ||
      persisted.lastActivityAt === 0
    ) {
      return;
    }
    return closeSessionAndEmitEnded(
      key,
      persisted.lastActivityAt,
      persisted.sessionStartedAt,
      postAuditEvent,
    );
  };

  const logout = async (
    isTestUser: boolean,
    redirectUrl: string,
    isClient: boolean,
    allSessions = true,
  ) => {
    let cognitoUsername: string | null = null;
    try {
      const currentUser: CognitoUser = await Auth.currentAuthenticatedUser();
      cognitoUsername = currentUser.getUsername();
    } catch {
      // Session already expired — continue with logout cleanup
    }

    const googleSignedInUser = window.localStorage.getItem(
      SIGNED_IN_WITH_GOOGLE_USER_KEY,
    );

    // if we are logging out a user, who was logged in through google
    // then we should clear the storage item for this user
    // this allows us to skip calling the hosted ui endpoint for the non federated users
    if (cognitoUsername === googleSignedInUser) {
      window.localStorage.removeItem(SIGNED_IN_WITH_GOOGLE_USER_KEY);
    }

    // When a client user signs in via google, the sessions are managed by the
    // unauth-router via auth.copilot.app
    //
    // This is done, since a client user can initiate sign in via google through different domains
    // We manage the redirects via the api at `auth.<main domain>`.
    // The cookies are set on this api domain which remembers the session and
    // redirects the user as required.
    //
    // If a client has multiple portals logged in via the same browser, they
    // effectively have multiple sessions being managed by the api.
    // When the logout is initiated for the client, the callback is received by the api
    // but there is no information about the session to log out of.
    //
    // To solve this we call the `/auth/google/initiate-logout?portalId=<>` api before logging out
    // This api sets a cookie
    // google-logout-init-for-portal with the value being the
    // portal id the user is logging out from
    //
    // Now when the logout is completed via the unauth-router's signout endpoint
    // the api can redirect the user to the correct redirectSignOutUrl based on the portalId
    // set here
    if (isClient && googleSignedInUser && !isEmptyString(googleSignedInUser)) {
      const redirectSignOutURL = portalConfig.AWS.Auth.oauth?.redirectSignOut;

      if (redirectSignOutURL) {
        // the init logout api uses the same api as the redirect signout api
        // the portalId is set in the query param
        const initLogoutApi = `${
          new URL(redirectSignOutURL).origin
        }/auth/google/initiate-logout?portalId=${portalConfig.portalHeader}`;
        await axios.get(initLogoutApi, { withCredentials: true });

        // Emit audit events before signout while JWT is still valid.
        // sessionEnded fires first so a partial failure dropping userLoggedOut
        // still leaves the session correctly closed.
        // Skip for proxied users — handled by redirectLogoutHandler.
        // Skip if session already expired (cognitoUsername null).
        const proxiedUser = window.localStorage.getItem(
          SIGNED_IN_WITH_PROXY_USER_KEY,
        );
        if (cognitoUsername && (!proxiedUser || isEmptyString(proxiedUser))) {
          await endSessionForLogout();
          await emitLogoutEvent();
        }

        // for proxied users,
        // this signs out the custom auth session
        // In this case to clear the google session and complete signout,
        // this has to be followed by the
        // `logoutFromHostedUi` which takes the user to the logout endpoint
        //
        // for non proxied user, this will clear the google session
        await Auth.signOut();

        // if a user was proxied in, that represents a valid session with google
        // to clear the google session , we need to log the user out from the
        // hosted ui
        if (proxiedUser && !isEmptyString(proxiedUser)) {
          window.localStorage.removeItem(SIGNED_IN_WITH_PROXY_USER_KEY);
          resetPosthogIdentity();
          logoutFromHostedUi(portalConfig, redirectSignOutURL);
          return;
        }
      }
    } else {
      // Emit audit events while JWT is still valid, before session teardown.
      // sessionEnded fires first so a partial failure dropping userLoggedOut
      // still leaves the session correctly closed.
      // Only attempt if we have a valid session (cognitoUsername not null).
      if (cognitoUsername) {
        await endSessionForLogout();
        await emitLogoutEvent();
      }

      // For internal users, end server sessions before Auth.signOut().
      // Auth.signOut() queues a Cognito OAuth redirect; any subsequent
      // await gives the browser idle time to process it, causing the page
      // to unload before endSessions completes. If portal session cookies
      // survive, the SSR middleware redirects /login → / (stale session).
      if (!isClient) {
        const endFc = allSessions
          ? UsersClient.endSessions
          : UsersClient.endCurrentSession;
        await endFc();
      }
      await Auth.signOut();
    }

    TrackEvent(`Signed Out`, { isTestUser });
    // clients should be redirected to the SignoutRedirectURL internal users have set,
    // whether that is within copilot domain or a marketing site of their choice
    if (isClient) {
      resetPosthogIdentity();
      window.location.replace(`/auth/signout?next=${redirectUrl}`);
    } else {
      // if the user was proxied in, we need to clear the proxy session flag
      if (cognitoUsername) {
        window.localStorage.removeItem(`proxy#${cognitoUsername}`);
      }

      // if there was a google sign in done, and we are trying to log out of all sessions
      // we need to call the hosted ui logout endpoint, to complete the login
      // this ensures that the next time, we try logging in with google an account selection
      // screen is shown
      if (
        allSessions &&
        googleSignedInUser &&
        !isEmptyString(googleSignedInUser)
      ) {
        // since all the user logged in with google will be signed out, we clear the
        // storage
        window.localStorage.removeItem(SIGNED_IN_WITH_GOOGLE_USER_KEY);

        // logs any google authed user out
        resetPosthogIdentity();
        logoutFromHostedUi(portalConfig, window.location.origin + redirectUrl);
        return;
      }

      resetPosthogIdentity();
      window.location.replace(redirectUrl);
    }
  };
  return logout;
};
